import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Check, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import type { RegistrationInfoDiscount as EventCheckoutSessionDiscount } from "@/modules/eventRegistration/features/EventRegistration/api/getEventSessionRegistrationInfo/types";

interface DiscountCodeRedemptionProps {
  allAvailableDiscounts: EventCheckoutSessionDiscount[];
  onDiscountApplied: (discountData: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  }) => void;
  onDiscountRemoved: () => void;
  isDiscountApplied: boolean;
  appliedDiscountInfo?: {
    code: string;
    type: "percentage" | "fixed_amount";
    value: number;
  } | null;
}

export default function DiscountCodeRedemption({
  isDiscountApplied,
  onDiscountApplied,
  onDiscountRemoved,
  allAvailableDiscounts,
  appliedDiscountInfo,
}: DiscountCodeRedemptionProps) {
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discountCode, setDiscountCode] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState(false);

  const handleApplyDiscount = () => {
    if (!discountCode.trim()) return;

    setIsLoading(true);
    setError(null);

    const matchedDiscount = allAvailableDiscounts.find(
      (d) => d.code?.toLowerCase() === discountCode.trim().toLowerCase(),
    );

    if (!matchedDiscount) {
      setError("Invalid or inapplicable discount code.");
      setIsLoading(false);
      return;
    }

    const discountVal = parseFloat(matchedDiscount.discountValue ?? "0") || 0;
    const discountType =
      (matchedDiscount.discountType as "percentage" | "fixed_amount") ??
      "fixed_amount";

    if (discountVal <= 0) {
      setError("Discount has no effect.");
      setIsLoading(false);
      return;
    }

    onDiscountApplied({
      code: matchedDiscount.code ?? "",
      type: discountType,
      value: discountVal,
    });

    const displayText =
      discountType === "percentage"
        ? `${discountVal}% off per seat`
        : `$${discountVal.toFixed(2)} off per seat`;

    toast({
      title: "Discount applied!",
      description: `Code "${matchedDiscount.code}" — ${displayText}`,
    });

    setIsLoading(false);
  };

  const handleRemoveDiscount = () => {
    onDiscountRemoved();
  };

  if (isDiscountApplied && appliedDiscountInfo) {
    const displayText =
      appliedDiscountInfo.type === "percentage"
        ? `${appliedDiscountInfo.value}% off per seat`
        : `$${appliedDiscountInfo.value.toFixed(2)} off per seat`;

    return (
      <div className="flex items-center gap-2 text-sm py-2">
        <Check className="h-4 w-4 text-blue-500 flex-shrink-0" />
        <span className="text-blue-700 dark:text-blue-300 font-medium">
          &quot;{appliedDiscountInfo.code}&quot; &mdash; {displayText}
        </span>
        <button
          className="ml-auto text-sm text-muted-foreground underline hover:no-underline"
          onClick={handleRemoveDiscount}
          type="button"
        >
          Remove
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium py-1"
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
      >
        {isExpanded ? (
          <ChevronUp className="h-3.5 w-3.5" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" />
        )}
        Have a promo code?
      </button>

      {isExpanded && (
        <div className="mt-3 pl-5">
          <div className="flex items-center gap-2">
            <Input
              className="flex-1 h-9"
              disabled={isLoading}
              id="discount-code"
              onChange={(e) => setDiscountCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleApplyDiscount();
                }
              }}
              placeholder="Enter code"
              type="text"
              value={discountCode}
            />
            <Button
              disabled={isLoading || !discountCode.trim()}
              onClick={handleApplyDiscount}
              size="sm"
              variant="outline"
            >
              {isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                "Apply"
              )}
            </Button>
          </div>

          {error && (
            <p className="text-sm text-destructive mt-2">{error}</p>
          )}
        </div>
      )}
    </div>
  );
}
